import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { api } from '@/services/api';
import { toast } from '@/store/useToast';
import { Mail, Lock, Loader } from 'lucide-react';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { webLogin, loading, error, clearError } = useStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const telegramBtnRef = useRef<HTMLDivElement>(null);
  const botUsername = import.meta.env.VITE_BOT_USERNAME;

  // Telegram Login Widget
  useEffect(() => {
    if (!botUsername || !telegramBtnRef.current) return;

    (window as any).onTelegramAuth = async (data: Record<string, unknown>) => {
      try {
        await api.telegramWidgetLogin(data);
        // Fetch user into store after login
        const { fetchUser } = useStore.getState();
        await fetchUser();
        navigate('/', { replace: true });
      } catch {
        toast.error('Ошибка входа через Telegram');
      }
    };

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth');
    script.setAttribute('data-request-access', 'write');
    script.async = true;
    telegramBtnRef.current.appendChild(script);

    return () => {
      delete (window as any).onTelegramAuth;
    };
  }, [botUsername, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await webLogin(email, password);
      navigate('/', { replace: true });
    } catch {
      // error is in store
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-telegram-bg px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">🎰</div>
          <h1 className="text-2xl font-bold text-telegram-text">Ruffle</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Платформа розыгрышей</p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-telegram-text mb-5">Вход в аккаунт</h2>

          {/* Telegram Widget */}
          {botUsername && (
            <>
              <div ref={telegramBtnRef} className="flex justify-center mb-4" />
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
                <span className="text-xs text-gray-400">или</span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
              </div>
            </>
          )}

          {/* Email / password form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-telegram-text mb-1">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-telegram-text text-sm focus:outline-none focus:ring-2 focus:ring-telegram-button"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-telegram-text mb-1">Пароль</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-telegram-text text-sm focus:outline-none focus:ring-2 focus:ring-telegram-button"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-telegram-button text-telegram-buttonText font-semibold py-2.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {loading ? <Loader size={16} className="animate-spin" /> : null}
              Войти
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
          Нет аккаунта?{' '}
          <Link to="/register" className="text-telegram-button font-medium hover:underline">
            Зарегистрироваться
          </Link>
        </p>
      </div>
    </div>
  );
};
